import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, toast } from '@zawarski/eti-ui';
import { Pencil, Trash2 } from 'lucide-react';
import EntryPageShell, { EntryContent } from '@components/EntryPageShell';
import ConfirmDeleteDialog from '@components/dialogs/ConfirmDeleteDialog';
import NotificationContactForm, { toFormValues } from '@components/NotificationContactForm';
import { PageError, PageLoading } from '@components/layout/PageScaffold';
import { useAccess } from '@hooks/useAccess';
import { errorMessage } from '@utils/index';
import type { NotificationDomain } from './domain';
import { useNotificationDomain } from './useNotificationDomain';

const NotificationView = ({ domain }: { domain: NotificationDomain }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const rowId = Number(id);
  const { isStateUser, canEdit } = useAccess();
  const d = useNotificationDomain(domain, rowId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const values = useMemo(() => (d.row ? toFormValues(d.row) : null), [d.row]);
  const canWrite = canEdit && (domain === 'equipment' || isStateUser);

  const remove = async () => {
    try {
      await d.remove(rowId);
      toast.success('Successfully deleted notification entry. ');
      navigate(d.listPath);
    } catch (e) {
      toast.error(errorMessage(e, 'Error deleting notification.'));
    }
  };

  return (
    <EntryPageShell
      title='View Notification'
      subtitle={d.config.title}
      onBack={() => navigate(d.listPath)}
      footer={
        canWrite && d.row ? (
          <>
            <Button
              variant='destructive'
              startIcon={<Trash2 className='rep:size-4' />}
              onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
            <Button
              startIcon={<Pencil className='rep:size-4' />}
              onClick={() => navigate(d.editPath(rowId))}>
              Edit
            </Button>
          </>
        ) : undefined
      }>
      {d.rowLoading ? (
        <PageLoading />
      ) : d.rowError || !values ? (
        <PageError message='There was an error loading the contact' />
      ) : (
        <EntryContent>
          <NotificationContactForm
            values={values}
            onChange={() => undefined}
            errors={{}}
            saveAttempted={false}
            triggerOptions={d.triggerOptions}
            triggerLabel={d.config.trigger}
            readOnly
          />
        </EntryContent>
      )}
      <ConfirmDeleteDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title='Delete contact?'
        message={`${d.row?.pvFullName || 'This contact'} will no longer receive notifications.`}
        processing={d.deleting}
        onConfirm={() => void remove()}
      />
    </EntryPageShell>
  );
};

export default NotificationView;
